import { ComponentType } from 'react'
import {
  ErrorRenderer,
  SmartRenderer,
  SearchActivitiesRenderer,
  CalculationResultRenderer,
  ScenarioResultRenderer,
  isScenarioResult,
  TaskStatusRenderer,
  SearchMethodsRenderer,
  ReadActivityRenderer,
  AgentRenderer,
  WebSearchRenderer,
  WebFetchRenderer,
} from './renderers'

// Common props for all tool renderers
export interface ToolRendererProps {
  data: unknown
  success?: boolean
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RendererData = any

// Registry of custom renderers by tool name
// Keys should match the tool_name from the MCP server
const rendererRegistry: Record<string, ComponentType<{ data: RendererData }>> = {
  // Search tools
  search_activities: SearchActivitiesRenderer,
  search_methods: SearchMethodsRenderer,
  search_biosphere: SmartRenderer, // Uses smart renderer for now

  // Calculation tools — handled dynamically in resolveRenderer (shape-dependent)
  // 'calculate_standard', 'calculate_custom', 'get_task_status'

  // Read tools
  read_activity: ReadActivityRenderer,

  // Reference tools (use SmartRenderer)
  get_units: SmartRenderer,
  get_biosphere_compartments: SmartRenderer,
  list_method_categories: SmartRenderer,
  validate_method: SmartRenderer,
  validate_unit: SmartRenderer,
  get_calculation_history: SmartRenderer,
  get_calculation_stats: SmartRenderer,

  // Power search
  advanced_search: SmartRenderer,
  get_graph_schema: SmartRenderer,

  // Agent tools (from EcoSemantic Agents MCP)
  scout: AgentRenderer,
  analyst: AgentRenderer,
  tracer: AgentRenderer,
  chat_with_custom_agent: AgentRenderer,

  // Scenario analysis (calculate_scenarios is shape-dependent, see resolveRenderer)
  create_scenario: SmartRenderer,
  list_scenarios: SmartRenderer,
  delete_scenario: SmartRenderer,
  rebase_scenario: SmartRenderer,

  // Custom modeling (most use SmartRenderer)
  create_project: SmartRenderer,
  list_projects: SmartRenderer,
  get_project: SmartRenderer,
  list_activities: SearchActivitiesRenderer, // Can reuse activity renderer
  get_activity: ReadActivityRenderer,
  list_exchanges: SmartRenderer,

  // === External MCP tools ===

  // Parallel Search (web search & fetch)
  web_search_preview: WebSearchRenderer,
  web_fetch: WebFetchRenderer,
}

// Detect if data looks like an error
function isErrorResponse(data: unknown, success?: boolean): boolean {
  if (success === false) return true
  if (data === null || data === undefined) return false

  if (typeof data === 'object') {
    const obj = data as Record<string, unknown>
    // Common error indicators
    if ('error' in obj && obj.error) return true
    if ('error_code' in obj && obj.error_code) return true
    if ('error_category' in obj && obj.error_category) return true
    if ('status_code' in obj && (obj.status_code as number) >= 400) return true
    if (Array.isArray(obj.error)) return true // Validation errors
    // FastAPI validation error format
    if (Array.isArray(data) && (data as Record<string, unknown>[])[0]?.msg) return true
  }

  return false
}

// Check if data matches what CalculationResultRenderer expects
function isCalculationResult(data: unknown): boolean {
  if (typeof data !== 'object' || data === null) return false
  const obj = data as Record<string, unknown>
  return typeof obj.lca_score === 'number' && typeof obj.score_unit === 'string'
}

// Check if data looks like a task status response
function isTaskStatus(data: unknown): boolean {
  if (typeof data !== 'object' || data === null) return false
  const obj = data as Record<string, unknown>
  return typeof obj.task_id === 'string' && typeof obj.status === 'string'
}

/**
 * Resolves the appropriate renderer component for a given tool name and data.
 *
 * Priority:
 * 1. If error response detected → ErrorRenderer
 * 2. If custom renderer exists for tool → Custom renderer
 * 3. Fallback → SmartRenderer (heuristic-based)
 */
export function resolveRenderer(
  toolName: string,
  data: unknown,
  success?: boolean,
): ComponentType<{ data: RendererData }> {
  // Check for error response first
  if (isErrorResponse(data, success)) {
    return ErrorRenderer
  }

  // For calculation tools, verify data shape before using specialized renderer
  if (toolName === 'calculate_standard' || toolName === 'calculate_custom') {
    if (isCalculationResult(data)) return CalculationResultRenderer
    if (isTaskStatus(data)) return TaskStatusRenderer
    return SmartRenderer
  }

  // calculate_scenarios queues a task; the polled result is scenario-shaped
  if (toolName === 'calculate_scenarios') {
    if (isScenarioResult(data)) return ScenarioResultRenderer
    if (isTaskStatus(data)) return TaskStatusRenderer
    return SmartRenderer
  }

  // For get_task_status, check if it has a completed result
  if (toolName === 'get_task_status') {
    if (isScenarioResult(data)) return ScenarioResultRenderer
    if (isCalculationResult(data)) return CalculationResultRenderer
    return TaskStatusRenderer
  }

  // Look up custom renderer
  const customRenderer = rendererRegistry[toolName]
  if (customRenderer) {
    return customRenderer
  }

  // Fallback to smart renderer
  return SmartRenderer
}
